// @ts-nocheck
// One call-centre board: KPI strip + alerts + direction / phase filters +
// DataTable + detail sheet. Used by the call-centre admin view, by the
// platform / company view once a call centre is picked, and by the agent
// view (own calls, no actions).
import React, { useCallback, useMemo, useState } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmptyState } from '@/components/feedback/EmptyState';
import { LiveDot, Panel } from '@/features/cti2/team/teamUi';
import { useLiveCalls } from '../useLiveCalls';
import type { LiveCallsQuery } from '../types';
import { buildAlerts, countCalls, filterByPhase, filterByText, type PhaseFilter } from '../liveCallsModel';
import LiveCallsKpiStrip from './LiveCallsKpiStrip';
import LiveCallsAlertsBar from './LiveCallsAlertsBar';
import LiveCallsTable from './LiveCallsTable';
import LiveCallDetailSheet from './LiveCallDetailSheet';

interface Props {
  tableId: string;
  query: LiveCallsQuery;
  enabled?: boolean;
  /** Pass useSnoop() to render supervise buttons (team leads). */
  snoop?: any;
  canSnoop?: boolean;
  /** Hide the direction tab row (agent / team-lead scopes have no per-direction keys). */
  hideDirectionTabs?: boolean;
  compactKpis?: boolean;
}

export function TransportLabel({ transport, stale }: { transport: string; stale?: number }) {
  const label = transport === 'stream' ? 'live stream' : transport === 'poll' ? 'polling 3 s' : 'paused';
  return (
    <span className="inline-flex items-center gap-2 text-[11px] text-muted-foreground">
      <LiveDot tone={transport === 'off' ? 'off' : 'good'} label={label} />
      {stale ? <span>· {stale} stale skipped</span> : null}
    </span>
  );
}

export default function LiveCallsBoard({ tableId, query, enabled = true, snoop, canSnoop = false, hideDirectionTabs = false, compactKpis = false }: Props) {
  const [direction, setDirection] = usePersistedState<'all' | 'in' | 'out' | 'int'>(`live-calls/${tableId}:direction`, 'all');
  const [phase, setPhase] = usePersistedState<PhaseFilter>(`live-calls/${tableId}:phase`, 'all');
  const [text, setText] = usePersistedState(`live-calls/${tableId}:text`, '');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const effectiveQuery = useMemo(
    () => ({ ...query, direction: direction === 'all' ? undefined : direction, limit: query.limit || 100 }),
    [JSON.stringify(query), direction],
  );
  const feed = useLiveCalls(effectiveQuery, enabled);
  const calls = feed.data?.calls || [];

  const counters = useMemo(() => countCalls(calls), [calls]);
  const shown = useMemo(() => filterByText(filterByPhase(calls, phase), text), [calls, phase, text]);
  const alerts = useMemo(() => buildAlerts(calls, feed.now), [calls, feed.now]);
  const selected = useMemo(() => calls.find((c) => c.id === selectedId) || null, [calls, selectedId]);
  const pick = useCallback((id: string) => setSelectedId(id), []);

  if (feed.error && !feed.data) {
    return <EmptyState title="Live calls unavailable" description={feed.error} action={<Button variant="outline" size="sm" onClick={feed.refresh}>Retry</Button>} />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <LiveCallsKpiStrip counters={counters} counts={feed.data?.counts} phase={phase} onPhase={setPhase} compact={compactKpis} />

      <Panel className="p-3">
        <LiveCallsAlertsBar alerts={alerts} onPick={pick} />
      </Panel>

      <LiveCallsTable
        tableId={tableId}
        calls={shown}
        now={feed.now}
        isLoading={feed.loading}
        onRowClick={(c) => setSelectedId(c.id)}
        snoop={snoop}
        canSnoop={canSnoop}
        leftContent={
          <div className="flex flex-wrap items-center gap-2">
            {!hideDirectionTabs ? (
              <Tabs value={direction} onValueChange={(v) => setDirection(v)}>
                <TabsList>
                  <TabsTrigger value="all">All{feed.data?.counts ? ` ${feed.data.counts.total}` : ''}</TabsTrigger>
                  <TabsTrigger value="in">In{feed.data?.counts ? ` ${feed.data.counts.in}` : ''}</TabsTrigger>
                  <TabsTrigger value="out">Out{feed.data?.counts ? ` ${feed.data.counts.out}` : ''}</TabsTrigger>
                  <TabsTrigger value="int">Internal{feed.data?.counts ? ` ${feed.data.counts.int}` : ''}</TabsTrigger>
                </TabsList>
              </Tabs>
            ) : null}
            <Input
              id={`${tableId}-search`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Customer, DID, agent, queue…"
              className="h-8 w-56 text-xs"
            />
            <TransportLabel transport={feed.transport} stale={feed.data?.stale} />
            <Button variant="ghost" size="icon" className="h-8 w-8" title="Refresh" onClick={feed.refresh}>
              <RefreshCw className={feed.loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        }
      />

      <LiveCallDetailSheet call={selected} now={feed.now} open={Boolean(selectedId)} onOpenChange={(o) => !o && setSelectedId(null)} />
    </div>
  );
}
